import { useEffect, useRef } from "react";
import { DropListWithIconItem, MarketPair } from "@cede/types";
import { useTradeForm } from "../form/useTradeForm";

type UseAutoSelectTokensProps = {
  assetsTo: DropListWithIconItem[];
  assetsFrom: DropListWithIconItem[];
  markets: MarketPair[];
  isTokenToLocked: boolean;
  enabled?: boolean;
};

/**
 * @remarks
 * This hook ONLY handles INITIAL token selection when the widget loads.
 *
 * It runs ONCE per account+config combination when:
 * - assetsFrom become available for the first time
 * - Account changes (new account selected)
 * - Config changes (new campaign/recommended token)
 *
 * After initial selection is complete, this hook does NOT run again for that account+config.
 *
 * TokenFrom selection priority:
 * 1. If recommendedTokenFrom is specified, select it directly
 * 2. If recommendedToken exists in assetsFrom, select it directly
 * 3. Select token with highest USD value that has a trading market with recommendedToken
 * 4. Otherwise, select the first token from assetsFrom (highest USD value)
 *
 * TokenTo selection priority:
 * 1. If recommendedToken exists and differs from tokenFrom, select recommendedToken
 * 2. If tokenFrom is the same as recommendedToken, select the first available token from assetsTo
 */
export const useAutoSelectTokens = ({
  assetsTo,
  assetsFrom,
  markets,
  isTokenToLocked,
  enabled = true,
}: UseAutoSelectTokensProps) => {
  const { tradeForm, dispatchForm } = useTradeForm();
  const config = tradeForm.initialProps;

  // Track what we've already initialized for to prevent re-running
  // Format: "accountId|recommendedToken|recommendedTokenFrom"
  const initializedForRef = useRef<string | null>(null);

  // Build the signature for current account+config
  const currentSignature = [
    tradeForm.selectedAccount?.value ?? "",
    config?.recommendedToken ?? "",
    config?.recommendedTokenFrom ?? "",
  ].join("|");

  // Effect for initial token selection - runs when data becomes available or account/config changes
  useEffect(() => {
    if (!enabled) return;

    const hasRequiredData = assetsFrom.length > 0 && markets.length > 0;
    if (!hasRequiredData) return;

    const isNewAccountOrConfig = initializedForRef.current !== currentSignature;
    const needsInitialSelection = !tradeForm.tokenFrom || !tradeForm.tokenTo;

    // Only run if:
    // 1. Account or config changed (new context), OR
    // 2. No tokens are selected yet (initial load)
    if (!isNewAccountOrConfig && !needsInitialSelection) return;

    // If this is a new account/config context and we've initialized before, reset first
    if (isNewAccountOrConfig && initializedForRef.current !== null) {
      dispatchForm({ field: "tokenFrom", value: null });
      dispatchForm({ field: "tokenTo", value: null });
      dispatchForm({ field: "market", value: null });
      dispatchForm({ field: "orderSide", value: null });
      dispatchForm({ field: "noCommonMarket", value: false });
      dispatchForm({ field: "amountFrom", value: "0" });
      dispatchForm({ field: "amountTo", value: "0" });
      // Mark as initialized and return - next effect run will do the selection
      initializedForRef.current = currentSignature;
      return;
    }

    // Mark as initialized for this account+config
    initializedForRef.current = currentSignature;

    // Select tokenFrom
    let selectedTokenFrom: DropListWithIconItem | null = null;

    // Priority 1: If recommendedTokenFrom is specified, select it directly
    if (config?.recommendedTokenFrom) {
      const recommendedAsset = assetsFrom.find((asset) => asset.value === config.recommendedTokenFrom);
      if (recommendedAsset) {
        selectedTokenFrom = recommendedAsset;
      }
    }

    // Priority 2: Try to match the recommended token
    if (!selectedTokenFrom && config?.recommendedToken) {
      for (const asset of assetsFrom) {
        // Select in priority the recommended token if it is present in the assetsFrom
        if (asset.value === config.recommendedToken) {
          selectedTokenFrom = asset;
          break;
        }

        // Priority 3: Select token with highest USD value that has a market with recommendedToken
        const commonMarket = markets.find(
          (market: MarketPair) =>
            (market.base === asset.value && market.quote === config.recommendedToken) ||
            (market.base === config.recommendedToken && market.quote === asset.value),
        );
        if (commonMarket) {
          selectedTokenFrom = asset;
          break;
        }
      }
    }

    // Fallback: select the first token from assetsFrom (highest USD value)
    if (!selectedTokenFrom && assetsFrom[0]) {
      selectedTokenFrom = assetsFrom[0];
    }

    if (!selectedTokenFrom) return;

    // Dispatch tokenFrom selection
    dispatchForm({ field: "tokenFrom", value: selectedTokenFrom });

    // Select tokenTo based on the selected tokenFrom
    if (assetsTo.length === 0) return;

    let selectedTokenTo: DropListWithIconItem | null = null;

    // Priority 1: If tokenFrom is not the recommendedToken, select recommendedToken as tokenTo
    if (config?.recommendedToken && selectedTokenFrom.value !== config.recommendedToken) {
      const asset = assetsTo.find((a: DropListWithIconItem) => a.value === config.recommendedToken);
      if (asset) {
        selectedTokenTo = asset;
      }
    }

    // Priority 2: If tokenFrom IS the recommendedToken, select first available from assetsTo
    if (!selectedTokenTo) {
      // Filter out tokenFrom (can't trade same token to itself)
      const availableAssetsTo = assetsTo.filter((asset) => asset.value !== selectedTokenFrom.value);

      if (!isTokenToLocked && availableAssetsTo.length > 0) {
        // Choose by default the token with a common market with the tokenFrom
        const commonMarkets = markets.filter(
          (market: MarketPair) => market.base === selectedTokenFrom.value || market.quote === selectedTokenFrom.value,
        );
        const assetTo = availableAssetsTo.find((asset: DropListWithIconItem) =>
          commonMarkets.some((market: MarketPair) => market.quote === asset.value || market.base === asset.value),
        );
        if (assetTo) {
          selectedTokenTo = assetTo;
        }
      }

      // Fallback: select first available token from assetsTo (excluding tokenFrom)
      if (!selectedTokenTo && availableAssetsTo.length > 0) {
        selectedTokenTo = availableAssetsTo[0] ?? null;
      }
    }

    if (selectedTokenTo) {
      dispatchForm({ field: "tokenTo", value: selectedTokenTo });
    }
  }, [
    enabled,
    currentSignature,
    assetsFrom,
    assetsTo,
    markets,
    config?.recommendedToken,
    config?.recommendedTokenFrom,
    isTokenToLocked,
    tradeForm.tokenFrom,
    tradeForm.tokenTo,
    dispatchForm,
  ]);
};
